import { AxiosResponse } from 'axios';
import {
  RemoteExecutionServiceImpl,
  RemoteGETCallInputObject,
  RemotePOSTCallInputObject,
  ServerResponse,
} from '../../../remoteExecution/RemoteExecutionServiceImpl';
import {
  AssignWorkerOnboardingChecklistInstanceRequestDTO,
  CreateWorkerOnboardingChecklistTemplateRequestDTO,
  UpdateWorkerOnboardingChecklistStepStatusRequestDTO,
  UpdateWorkerOnboardingChecklistTemplateRequestDTO,
  WorkerOnboardingChecklistInstanceResponseDTO,
  WorkerOnboardingChecklistInstancesByWorkerResponseDTO,
  WorkerOnboardingChecklistTemplateResponseDTO,
} from './WorkerOnboardingChecklistInterfaces';

const remoteCallObject = RemoteExecutionServiceImpl();
const backendApiBasePathLocal: string = process.env.REACT_APP_BACK_END_SERVER_BASE_PATH_LOCAL ?? '';

const WORKER_ONBOARDING_CHECKLIST_TEMPLATE_BASE_PATH: string =
  `${backendApiBasePathLocal}v1/agency/worker-onboarding-checklist-template`;

const WORKER_ONBOARDING_CHECKLIST_INSTANCE_BASE_PATH: string =
  `${backendApiBasePathLocal}v1/agency/worker-onboarding-checklist-instance`;

const CreateWorkerOnboardingChecklistTemplateAPICall = async (
  createRequest: CreateWorkerOnboardingChecklistTemplateRequestDTO
): Promise<WorkerOnboardingChecklistTemplateResponseDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<CreateWorkerOnboardingChecklistTemplateRequestDTO> = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_TEMPLATE_BASE_PATH}/create-worker-onboarding-checklist-template`,
    headers: {},
    object: createRequest,
  };

  const response: AxiosResponse<ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO, Error>,
      CreateWorkerOnboardingChecklistTemplateRequestDTO
    >(postCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const ListWorkerOnboardingChecklistTemplatesAPICall = async (): Promise<
  readonly WorkerOnboardingChecklistTemplateResponseDTO[] | null
> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_TEMPLATE_BASE_PATH}/worker-onboarding-checklist-templates`,
    headers: {},
    queryParams: null,
  };

  const response: AxiosResponse<ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO[], Error>> =
    await remoteCallObject.remoteGETCallV2<
      ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO[], Error>
    >(getCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const GetWorkerOnboardingChecklistTemplateByIdAPICall = async (
  checklistTemplateId: string
): Promise<WorkerOnboardingChecklistTemplateResponseDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_TEMPLATE_BASE_PATH}/worker-onboarding-checklist-template`,
    headers: {},
    queryParams: { checklistTemplateId },
  };

  const response: AxiosResponse<ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO, Error>> =
    await remoteCallObject.remoteGETCallV2<
      ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO, Error>
    >(getCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const UpdateWorkerOnboardingChecklistTemplateAPICall = async (
  updateRequest: UpdateWorkerOnboardingChecklistTemplateRequestDTO
): Promise<WorkerOnboardingChecklistTemplateResponseDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateWorkerOnboardingChecklistTemplateRequestDTO> = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_TEMPLATE_BASE_PATH}/update-worker-onboarding-checklist-template`,
    headers: {},
    object: updateRequest,
  };

  const response: AxiosResponse<ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<WorkerOnboardingChecklistTemplateResponseDTO, Error>,
      UpdateWorkerOnboardingChecklistTemplateRequestDTO
    >(postCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const AssignWorkerOnboardingChecklistInstanceAPICall = async (
  assignRequest: AssignWorkerOnboardingChecklistInstanceRequestDTO
): Promise<WorkerOnboardingChecklistInstanceResponseDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<AssignWorkerOnboardingChecklistInstanceRequestDTO> = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_INSTANCE_BASE_PATH}/assign-worker-onboarding-checklist-instance`,
    headers: {},
    object: assignRequest,
  };

  const response: AxiosResponse<ServerResponse<WorkerOnboardingChecklistInstanceResponseDTO, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<WorkerOnboardingChecklistInstanceResponseDTO, Error>,
      AssignWorkerOnboardingChecklistInstanceRequestDTO
    >(postCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const ListWorkerOnboardingChecklistInstancesByWorkerAPICall = async (
  primaryWorkyUserId: string
): Promise<WorkerOnboardingChecklistInstancesByWorkerResponseDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_INSTANCE_BASE_PATH}/worker-onboarding-checklist-instances-by-worker`,
    headers: {},
    queryParams: { primaryWorkyUserId },
  };

  const response: AxiosResponse<
    ServerResponse<WorkerOnboardingChecklistInstancesByWorkerResponseDTO, Error>
  > = await remoteCallObject.remoteGETCallV2<
    ServerResponse<WorkerOnboardingChecklistInstancesByWorkerResponseDTO, Error>
  >(getCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const GetWorkerOnboardingChecklistInstanceByIdAPICall = async (
  checklistInstanceId: string
): Promise<WorkerOnboardingChecklistInstanceResponseDTO | null> => {
  const getCallInput: RemoteGETCallInputObject = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_INSTANCE_BASE_PATH}/worker-onboarding-checklist-instance`,
    headers: {},
    queryParams: { checklistInstanceId },
  };

  const response: AxiosResponse<ServerResponse<WorkerOnboardingChecklistInstanceResponseDTO, Error>> =
    await remoteCallObject.remoteGETCallV2<
      ServerResponse<WorkerOnboardingChecklistInstanceResponseDTO, Error>
    >(getCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const UpdateWorkerOnboardingChecklistStepStatusAPICall = async (
  updateRequest: UpdateWorkerOnboardingChecklistStepStatusRequestDTO
): Promise<WorkerOnboardingChecklistInstanceResponseDTO | null> => {
  const postCallInput: RemotePOSTCallInputObject<UpdateWorkerOnboardingChecklistStepStatusRequestDTO> = {
    apiUrl: `${WORKER_ONBOARDING_CHECKLIST_INSTANCE_BASE_PATH}/update-worker-onboarding-checklist-instance-step-status`,
    headers: {},
    object: updateRequest,
  };

  const response: AxiosResponse<ServerResponse<WorkerOnboardingChecklistInstanceResponseDTO, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<WorkerOnboardingChecklistInstanceResponseDTO, Error>,
      UpdateWorkerOnboardingChecklistStepStatusRequestDTO
    >(postCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

const DeleteWorkerOnboardingChecklistInstanceAPICall = async (
  checklistInstanceId: string
): Promise<string | null> => {
  const postCallInput: RemotePOSTCallInputObject<Record<string, never>> = {
    apiUrl:
      `${WORKER_ONBOARDING_CHECKLIST_INSTANCE_BASE_PATH}/delete-worker-onboarding-checklist-instance`
      + `?checklistInstanceId=${encodeURIComponent(checklistInstanceId)}`,
    headers: {},
    object: {},
  };

  const response: AxiosResponse<ServerResponse<string, Error>> =
    await remoteCallObject.remotePOSTCallV2<
      ServerResponse<string, Error>,
      Record<string, never>
    >(postCallInput);

  if (response?.data?.data) {
    return response.data.data;
  }

  return null;
};

export {
  CreateWorkerOnboardingChecklistTemplateAPICall,
  ListWorkerOnboardingChecklistTemplatesAPICall,
  GetWorkerOnboardingChecklistTemplateByIdAPICall,
  UpdateWorkerOnboardingChecklistTemplateAPICall,
  AssignWorkerOnboardingChecklistInstanceAPICall,
  ListWorkerOnboardingChecklistInstancesByWorkerAPICall,
  GetWorkerOnboardingChecklistInstanceByIdAPICall,
  UpdateWorkerOnboardingChecklistStepStatusAPICall,
  DeleteWorkerOnboardingChecklistInstanceAPICall,
};
